import './meat-capture'
import './meat-join'
import './meat-agent-card'
import './meat-window-card'
import type { AgentWidget, ChatMessage } from '../agent'
import type { OpComponent } from '../ot'
import type { WindowCard } from './meat-window-card'
import { trackAudioLevel } from '../audio-level'
import { keycloak, userName } from '../auth'

const template = document.createElement('template')
template.innerHTML = `
  <style>
    :host {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
      gap: var(--space-4);
      width: min(100vw, 80rem);
      padding: var(--space-4);
      box-sizing: border-box;
    }
    /* Without agents, everyone shares one grid, starting with ourselves. */
    #stage { display: none; }
    #feeds { display: contents; }
    meat-capture { order: -1; }

    /*
     * An agent takes the middle like a screen share. Everyone else stacks down
     * the right, with our own camera tucked in the bottom corner.
     */
    :host([presenting]) {
      grid-template-columns: minmax(0, 1fr) 12rem;
      grid-template-rows: minmax(0, 1fr) auto;
      grid-template-areas:
        "stage feeds"
        "stage self";
      width: 100vw;
      height: 100dvh;
    }
    :host([presenting]) #stage {
      grid-area: stage;
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: minmax(0, 1fr);
      gap: var(--space-4);
      min-height: 0;
    }
    /* Agents' windows sit beside the agents, after them. */
    meat-window-card {
      order: 1;
    }
    :host([presenting]) #feeds {
      grid-area: feeds;
      display: flex;
      flex-direction: column;
      gap: var(--space-4);
      min-height: 0;
      overflow-y: auto;
      /* Room for the speaking ring, which the scroll would otherwise clip. */
      padding: var(--speaking-width-max);
      margin: calc(-1 * var(--speaking-width-max));
    }
    :host([presenting]) #feeds video {
      flex: none;
    }
    :host([presenting]) meat-capture {
      grid-area: self;
    }
    video {
      display: block;
      width: 100%;
      aspect-ratio: 4 / 3;
      object-fit: cover;
      border-radius: var(--radius-md);
      background: var(--color-media);
      /* Speaking indicator. An outline grows outward without shifting the layout. */
      --speaking-width: calc(var(--speaking-width-min) + var(--audio-level, 0) * (var(--speaking-width-max) - var(--speaking-width-min)));
      outline: var(--speaking-width) solid var(--color-accent);
    }
  </style>
  <div id="stage"></div>
  <div id="feeds"></div>
`

interface AgentInfo {
  id: string
  name: string
  description?: string
  draft: { revision: number; text: string }
}

type Signal =
  | { event: 'offer'; data: RTCSessionDescriptionInit }
  | { event: 'candidate'; data: RTCIceCandidateInit }
  | { event: 'agents'; data: AgentInfo[] }
  | { event: 'message'; data: ChatMessage }
  | { event: 'reply-chunk'; data: { agent: string; id: string; inReplyTo?: string; text: string } }
  | { event: 'thinking'; data: { agent: string; id: string; text: string } }
  | { event: 'tool-call'; data: { agent: string; callId: string; name: string; arguments: string } }
  | { event: 'tool-result'; data: { agent: string; callId: string; output: string } }
  | { event: 'draft-edit'; data: { agent: string; op: OpComponent[] } }
  | { event: 'draft-ack'; data: { agent: string } }
  | { event: 'features'; data: { voice: boolean } }
  | { event: 'voice-result'; data: { agent: string; text: string; error?: string } }
  | { event: 'voice-prompt'; data: { agent: string; peer: string; name: string; stream?: string; active: boolean } }
  | { event: 'window-open'; data: WindowInfo }
  | { event: 'window-close'; data: WindowRef }

interface WindowRef {
  agent: string
  id: string
}

interface WindowInfo extends WindowRef {
  title: string
  contentType: string
  // Base64, or null when empty.
  content: string | null
}

/**
 * Asks for a name, then joins the meeting named by the `slug` attribute as
 * it, publishes the local camera and shows everyone else's. While an agent is in the room the host has the
 * `presenting` attribute and the agent's widget takes center stage. So does
 * each window an agent opens, in a card of its own.
 */
export class MeatRoom extends HTMLElement {
  #root: ShadowRoot
  #stage: HTMLElement
  #feedList: HTMLElement
  #conn?: RTCPeerConnection
  #ws?: WebSocket
  // The name we join as, once it's been given.
  #name?: string
  #feeds = new Map<string, HTMLVideoElement>()
  // Our own camera and mic.
  #stream?: MediaStream
  #agents = new Map<string, AgentWidget>()
  // Agents' open windows, by windowKey().
  #windows = new Map<string, WindowCard>()
  // Whether agents can be prompted by voice.
  #voice = false
  // Signals are handled one at a time so a candidate can't race its offer.
  #queue = Promise.resolve()
  // Candidates that arrive before the first offer has been applied.
  #pendingCandidates: RTCIceCandidateInit[] = []

  constructor() {
    super()
    this.#root = this.attachShadow({ mode: 'open' })
    this.#root.append(template.content.cloneNode(true))
    this.#stage = this.#root.getElementById('stage')!
    this.#feedList = this.#root.getElementById('feeds')!
    this.#root.addEventListener('join', e => {
      ;(e.target as Element).remove()
      this.#join((e as CustomEvent<string>).detail)
    })
    this.#root.addEventListener('capture-ready', e => {
      this.#connect((e as CustomEvent<MediaStream>).detail)
        .catch(err => console.error('Error joining room', err))
    })
  }

  connectedCallback() {
    if (!this.slug) {
      console.error('<meat-room> needs a slug attribute')
      return
    }
    if (this.#name || this.#root.querySelector('meat-join')) return
    // Logged-in users join under their account's name; otherwise ask for one.
    const name = userName()
    if (name) this.#join(name)
    else this.#root.append(document.createElement('meat-join'))
  }

  #join(name: string) {
    this.#name = name
    this.#root.append(document.createElement('meat-capture'))
  }

  disconnectedCallback() {
    this.#ws?.close()
    this.#conn?.close()
  }

  get slug() {
    return this.getAttribute('slug')
  }

  get server() {
    const server = this.getAttribute('server')
    if (server) return server
    // The dev server only serves the app, so signaling is on the Go server's port.
    if (DEV) return `ws://${location.hostname}:8080`
    return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}`
  }

  async #connect(stream: MediaStream) {
    // The server checks the token on join, so make sure it has a while left.
    try {
      await keycloak.updateToken(30)
    } catch {
      // The session has ended; logging in again comes back to this room.
      await keycloak.login()
      return
    }

    this.#stream = stream
    const conn = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
    })
    for (const track of stream.getTracks()) conn.addTrack(track, stream)

    conn.onicecandidate = ({ candidate }) => {
      if (candidate) this.#send('candidate', candidate.toJSON())
    }
    conn.ontrack = ({ streams }) => {
      for (const s of streams) this.#addFeed(s)
    }

    const query = new URLSearchParams({ name: this.#name!, token: keycloak.token! })
    const ws = new WebSocket(`${this.server}/rooms/${encodeURIComponent(this.slug!)}?${query}`)
    ws.onmessage = ({ data }) => {
      const msg: Signal = JSON.parse(data)
      this.#queue = this.#queue
        .then(() => this.#handle(msg))
        .catch(err => console.error('Error handling signal', msg.event, err))
    }
    ws.onclose = () => {
      console.warn('Disconnected from room')
      conn.close()
    }

    this.#conn = conn
    this.#ws = ws
  }

  async #handle(msg: Signal) {
    const conn = this.#conn!

    switch (msg.event) {
      case 'offer':
        await conn.setRemoteDescription(msg.data)
        await conn.setLocalDescription()
        this.#send('answer', conn.localDescription)
        for (const candidate of this.#pendingCandidates.splice(0)) {
          await conn.addIceCandidate(candidate)
        }
        break

      case 'candidate':
        if (conn.remoteDescription) await conn.addIceCandidate(msg.data)
        else this.#pendingCandidates.push(msg.data)
        break

      case 'agents':
        this.#setAgents(msg.data)
        break

      case 'message':
        this.#addMessage(msg.data)
        break

      case 'reply-chunk':
        this.#agents.get(msg.data.agent)?.addReplyChunk(msg.data.id, msg.data.text, msg.data.inReplyTo)
        break

      case 'thinking':
        this.#agents.get(msg.data.agent)?.addThinking(msg.data.id, msg.data.text)
        break

      case 'tool-call':
        this.#agents.get(msg.data.agent)?.addToolCall(msg.data.callId, msg.data.name, msg.data.arguments)
        break

      case 'tool-result':
        this.#agents.get(msg.data.agent)?.addToolResult(msg.data.callId, msg.data.output)
        break

      case 'draft-edit':
        this.#agents.get(msg.data.agent)?.prompt?.applyServer(msg.data.op)
        break

      case 'draft-ack':
        this.#agents.get(msg.data.agent)?.prompt?.serverAck()
        break

      case 'features':
        this.#voice = msg.data.voice
        for (const view of this.#agents.values()) view.voice.hidden = !this.#voice
        break

      case 'voice-result':
        this.#agents.get(msg.data.agent)?.voice.voiceResult(msg.data.text, msg.data.error)
        break

      case 'voice-prompt': {
        const { agent, peer, name, stream, active } = msg.data
        const audio = stream ? this.#feeds.get(stream)?.srcObject as MediaStream | undefined : undefined
        this.#agents.get(agent)?.setVoicePrompt?.(peer, name, audio, active)
        break
      }

      case 'window-open':
        this.#showWindow(msg.data)
        break

      case 'window-close':
        this.#closeWindow(msg.data)
        break
    }
  }

  // Adds a view for each agent that joined and removes those that left.
  #setAgents(agents: AgentInfo[]) {
    const ids = new Set(agents.map(a => a.id))
    for (const [id, view] of this.#agents) {
      if (ids.has(id)) continue
      view.remove()
      this.#agents.delete(id)
    }

    for (const agent of agents) {
      if (this.#agents.has(agent.id)) continue
      const view: AgentWidget = document.createElement('meat-agent-card')
      view.setAttribute('name', agent.name)
      if (agent.description) view.setAttribute('description', agent.description)
      view.prompt?.reset(agent.draft.revision, agent.draft.text)
      view.voice.hidden = !this.#voice
      view.mic = this.#stream
      view.addEventListener('draft-edit', e => {
        this.#send('draft-edit', { agent: agent.id, ...e.detail })
      })
      view.addEventListener('draft-send', () => {
        this.#send('draft-send', { agent: agent.id })
      })
      view.addEventListener('voice-start', () => {
        this.#send('voice-start', { agent: agent.id })
      })
      view.addEventListener('voice-stop', () => {
        this.#send('voice-stop', { agent: agent.id })
      })
      this.#agents.set(agent.id, view)
      this.#stage.append(view)
    }

    this.#updatePresenting()
  }

  // Shows an agent's window, opening a card for it if it isn't already.
  #showWindow(info: WindowInfo) {
    const key = windowKey(info)
    let card = this.#windows.get(key)
    if (!card) {
      const newCard = document.createElement('meat-window-card')
      // Closed here straight away. The server closes it for everyone else.
      newCard.addEventListener('close', () => {
        this.#send('window-close', { agent: info.agent, id: info.id })
        this.#closeWindow(info)
      })
      this.#windows.set(key, newCard)
      this.#stage.append(newCard)
      this.#updatePresenting()
      card = newCard
    }
    // Agents arrive before their windows, so the name's known by now.
    card.setAttribute('label', this.#agents.get(info.agent)?.getAttribute('name') ?? '')
    card.show(info.title, info.contentType, decodeBase64(info.content ?? ''))
  }

  #closeWindow(ref: WindowRef) {
    const key = windowKey(ref)
    this.#windows.get(key)?.remove()
    this.#windows.delete(key)
    this.#updatePresenting()
  }

  #updatePresenting() {
    this.toggleAttribute('presenting', this.#agents.size > 0 || this.#windows.size > 0)
  }

  #addMessage(msg: ChatMessage) {
    if (msg.agent) {
      this.#agents.get(msg.agent)?.addReply(msg)
      return
    }
    // A person's message goes to the agent it was sent to, or to all of them.
    const views = msg.to ? [this.#agents.get(msg.to)] : this.#agents.values()
    for (const view of views) view?.addPrompt(msg)
  }

  #send(event: string, data: unknown) {
    this.#ws?.send(JSON.stringify({ event, data }))
  }

  // Each remote participant arrives as one MediaStream holding their audio and video.
  #addFeed(stream: MediaStream) {
    if (this.#feeds.has(stream.id)) return

    const video = document.createElement('video')
    video.playsInline = true
    video.srcObject = stream
    // Browsers may block unmuted autoplay until the user interacts with the page.
    video.play().catch(() => {
      document.addEventListener('click', () => video.play(), { once: true })
    })

    const stopMeter = trackAudioLevel(stream, video)

    stream.addEventListener('removetrack', () => {
      if (stream.getTracks().length > 0) return
      stopMeter()
      video.remove()
      this.#feeds.delete(stream.id)
    })

    this.#feeds.set(stream.id, video)
    this.#feedList.append(video)
  }
}

// Window IDs are only unique within an agent's windows.
function windowKey(ref: WindowRef) {
  return JSON.stringify([ref.agent, ref.id])
}

function decodeBase64(base64: string): Uint8Array {
  return Uint8Array.from(atob(base64), c => c.charCodeAt(0))
}

customElements.define('meat-room', MeatRoom)

declare global {
  interface HTMLElementTagNameMap {
    'meat-room': MeatRoom
  }
}
